import { useEffect, useState } from 'react';
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, Star } from 'lucide-react';
import { toast } from 'sonner';
import { updateReview } from '@/api/reviews.api';

const RATING_LABELS = { 1: 'Poor', 2: 'Fair', 3: 'Good', 4: 'Very good', 5: 'Excellent' };
const MAX_COMMENT = 1000;

/**
 * Super Admin: edit a review's rating, comment and status.
 * Inactive reviews are hidden from the customer & restaurant apps and are not
 * counted in any rating.
 */
const EditReviewDialog = ({ review, open, onOpenChange, onSaved }) => {
    const [rating, setRating] = useState(5);
    const [hover, setHover] = useState(0);
    const [comment, setComment] = useState('');
    const [active, setActive] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!review || !open) return;
        setRating(Number(review.rating) || 5);
        setComment(review.comment || '');
        setActive(review.status !== 'INACTIVE');
        setHover(0);
    }, [review, open]);

    if (!review) return null;

    const shown = hover || rating;
    const unchanged =
        rating === Number(review.rating) &&
        comment.trim() === (review.comment || '').trim() &&
        active === (review.status !== 'INACTIVE');

    const save = async () => {
        if (!Number.isInteger(rating) || rating < 1 || rating > 5) {
            toast.error('Please choose a rating from 1 to 5');
            return;
        }
        try {
            setSaving(true);
            const res = await updateReview(review.id, {
                rating,
                comment: comment.trim(),
                status: active ? 'ACTIVE' : 'INACTIVE',
            });
            toast.success('Review updated');
            onSaved?.(res.data?.data);
            onOpenChange(false);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to update review');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => !saving && onOpenChange(v)}>
            <DialogContent className="sm:max-w-lg" data-testid="edit-review-dialog">
                <DialogHeader>
                    <DialogTitle>Edit Review</DialogTitle>
                    <DialogDescription>
                        {review.order?.orderNumber ? `Order #${review.order.orderNumber} · ` : ''}
                        {review.user?.name || 'Customer'}
                        {review.resturant?.name ? ` · ${review.resturant.name}` : ''}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-5 py-1">
                    {/* Rating */}
                    <div>
                        <Label>Rating</Label>
                        <div className="mt-2 flex items-center gap-3">
                            <div
                                className="flex items-center gap-1"
                                role="radiogroup"
                                aria-label="Rating"
                                onMouseLeave={() => setHover(0)}
                            >
                                {[1, 2, 3, 4, 5].map((n) => (
                                    <button
                                        key={n}
                                        type="button"
                                        role="radio"
                                        aria-checked={rating === n}
                                        aria-label={`${n} star${n > 1 ? 's' : ''}`}
                                        onClick={() => setRating(n)}
                                        onMouseEnter={() => setHover(n)}
                                        className="rounded p-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400"
                                        data-testid={`edit-review-star-${n}`}
                                    >
                                        <Star
                                            size={28}
                                            className={n <= shown ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}
                                        />
                                    </button>
                                ))}
                            </div>
                            <span className="text-sm font-medium text-gray-700">
                                {shown}/5 · {RATING_LABELS[shown]}
                            </span>
                        </div>
                    </div>

                    {/* Comment */}
                    <div>
                        <div className="flex items-center justify-between">
                            <Label htmlFor="edit-review-comment">Comment</Label>
                            <span className="text-xs text-gray-400">{comment.length}/{MAX_COMMENT}</span>
                        </div>
                        <Textarea
                            id="edit-review-comment"
                            rows={4}
                            maxLength={MAX_COMMENT}
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            placeholder="No comment"
                            className="mt-2"
                            data-testid="edit-review-comment"
                        />
                    </div>

                    {/* Status */}
                    <div className="flex items-start justify-between gap-4 rounded-lg border p-3">
                        <div>
                            <Label htmlFor="edit-review-status" className="font-semibold">
                                {active ? 'Active' : 'Inactive'}
                            </Label>
                            <p className="text-xs text-gray-500 mt-0.5">
                                {active
                                    ? 'Visible in the customer and restaurant apps and counted in ratings.'
                                    : 'Hidden from the customer and restaurant apps and not counted in ratings.'}
                            </p>
                        </div>
                        <Switch
                            id="edit-review-status"
                            checked={active}
                            onCheckedChange={setActive}
                            data-testid="edit-review-status"
                        />
                    </div>

                    {review.editedByAdminAt && review.originalRating != null && (
                        <p className="text-xs text-gray-500">
                            Customer's original: {review.originalRating}/5
                            {review.originalComment ? ` — “${review.originalComment}”` : ''}
                        </p>
                    )}
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
                        Cancel
                    </Button>
                    <Button
                        onClick={save}
                        disabled={saving || unchanged}
                        style={{ backgroundColor: '#C2410C' }}
                        className="text-white"
                        data-testid="edit-review-save"
                    >
                        {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        Save changes
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default EditReviewDialog;
